"use strict";
/**
 * FaceTec Bypass Utility for Development/Testing
 *
 * Provides bypass functionality for FaceTec biometric verification
 * during local development and testing.
 *
 * SECURITY WARNING: This should NEVER be enabled in production!
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.simulateFaceTecSuccess = exports.canEnableTestMode = exports.getBypassStatusMessage = exports.logBypassUsage = exports.shouldBypassFaceTec = exports.hasBypassPermission = exports.isTestModeEnabled = exports.BYPASS_SKYSIGN_AUTH_PERMISSION = void 0;
const store_1 = require("../store");
const constants_1 = require("../constants/constants");
// Permission constant for SkySign FaceTec bypass (snake_case per Sky ecosystem standards)
exports.BYPASS_SKYSIGN_AUTH_PERMISSION = "bypass_skysign_auth";
/**
 * Check if FaceTec bypass is enabled via environment variable
 * @returns true if test mode is enabled, false otherwise
 */
const isTestModeEnabled = () => {
    // SECURITY: Never allow test mode in production, regardless of env var
    if (process.env.NODE_ENV === "production")
        return false;
    return process.env.ENABLE_TEST_AUTH === "true";
};
exports.isTestModeEnabled = isTestModeEnabled;
/**
 * Check if user has the bypass_skysign_auth permission
 * @returns true if user has bypass permission, false otherwise
 */
const hasBypassPermission = () => {
    const { userState } = store_1.redux_store.getState();
    const user = userState === null || userState === void 0 ? void 0 : userState.user;
    const permissions = (user === null || user === void 0 ? void 0 : user.permissions) || {};
    const hasBypass = permissions[exports.BYPASS_SKYSIGN_AUTH_PERMISSION] === true;
    return hasBypass;
};
exports.hasBypassPermission = hasBypassPermission;
/**
 * Check if FaceTec should be bypassed for current session
 * @returns true if FaceTec should be bypassed
 */
const shouldBypassFaceTec = () => {
    // Check environment-based test mode bypass
    if ((0, exports.isTestModeEnabled)()) {
        return true;
    }
    // Check if user has byPassSkySignAuth permission (staff bypass)
    if ((0, exports.hasBypassPermission)()) {
        return true;
    }
    // Check if user accessed via mobile app
    // Mobile users are already authenticated via Sky Lifestyle app
    const { userState } = store_1.redux_store.getState();
    const accessAuth = userState === null || userState === void 0 ? void 0 : userState.accessAuth;
    if ((accessAuth === null || accessAuth === void 0 ? void 0 : accessAuth.view_type) === constants_1.VIEW_TYPE.MOBILE) {
        return true;
    }
    return false;
};
exports.shouldBypassFaceTec = shouldBypassFaceTec;
/**
 * Log FaceTec bypass usage for audit trail
 * @param action - The action being bypassed (e.g., "login", "document_signing")
 */
const logBypassUsage = (action) => {
    if (!(0, exports.isTestModeEnabled)())
        return;
    const { userState } = store_1.redux_store.getState();
    const user = userState === null || userState === void 0 ? void 0 : userState.user;
    // Audit log created but not displayed to avoid console noise
    const auditLog = {
        timestamp: new Date().toISOString(),
        action: `facetec_bypass_${action}`,
        user_id: (user === null || user === void 0 ? void 0 : user._id) || "unknown",
        user_email: (user === null || user === void 0 ? void 0 : user.email) || "unknown",
        environment: process.env.NODE_ENV || "unknown",
        hostname: window.location.hostname,
        test_mode: true,
    };
};
exports.logBypassUsage = logBypassUsage;
/**
 * Get bypass status message for UI display
 * @returns Status message object with text and style
 */
const getBypassStatusMessage = () => {
    if (!(0, exports.isTestModeEnabled)()) {
        return null;
    }
    return {
        text: "⚠️ Test Mode Active - Facial Recognition Bypassed",
        className: "text-yellow-600 bg-yellow-100 px-4 py-2 rounded-md font-medium",
        severity: "warning",
    };
};
exports.getBypassStatusMessage = getBypassStatusMessage;
/**
 * Check if the current environment allows test mode
 * @returns true if environment allows test mode (local/dev only)
 */
const canEnableTestMode = () => {
    const env = process.env.NODE_ENV;
    const allowedEnvironments = ["development", "local", "test"];
    return allowedEnvironments.includes(env || "");
};
exports.canEnableTestMode = canEnableTestMode;
/**
 * Auto-complete FaceTec verification in test mode
 * Use this to simulate successful FaceTec verification
 */
const simulateFaceTecSuccess = (dispatch) => {
    if (!(0, exports.isTestModeEnabled)()) {
        return false;
    }
    (0, exports.logBypassUsage)("simulated_verification");
    // Dispatch success actions (these would normally come from real FaceTec)
    // The actual action names depend on your Redux setup
    dispatch({ type: "user/setFaceTecAuthorized", payload: true });
    return true;
};
exports.simulateFaceTecSuccess = simulateFaceTecSuccess;
// Module-load guard: isTestModeEnabled() already hard-returns false in production,
// so no additional runtime check is needed here.
exports.default = {
    isTestModeEnabled: exports.isTestModeEnabled,
    shouldBypassFaceTec: exports.shouldBypassFaceTec,
    hasBypassPermission: exports.hasBypassPermission,
    logBypassUsage: exports.logBypassUsage,
    getBypassStatusMessage: exports.getBypassStatusMessage,
    canEnableTestMode: exports.canEnableTestMode,
    simulateFaceTecSuccess: exports.simulateFaceTecSuccess,
    BYPASS_SKYSIGN_AUTH_PERMISSION: exports.BYPASS_SKYSIGN_AUTH_PERMISSION,
};
